/**
 * @file   src\utils\helper.ts
 * @brief  This is a general helper file.
 * @date   June, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { BayApi, SubscriptionandwashpriceApi } from "./apiUrls";
import axios from "src/config/axiosConfig";
import { pageURLs } from "./pageUrls";
 import { useNavigate, useParams } from "./thirdpartyComponents";
import { ServiceType } from "./enums";
import { IBayType } from "src/interfaces/bayinterface";

// const params = useParams();




// Format phone number and locale helpers — re-exported from countryHelper.
export {
  formatPhoneNumber,
  formatPhoneNumberforapipass,
  formatMoney,
  formatMoneyParts,
  getActiveLocale,
  setActiveLocale,
  getActiveCountryCode,
  getActiveCurrency,
  getActiveStripeCountry,
  getCurrencySymbol,
} from './countryHelper';

import { formatMoney as formatMoneyByLocale } from './countryHelper';

// Format credit card number string to '0000 0000 0000 0000' format
export const formatCreditCardNumber = (cardNumber: string) => {
  let formattedCardNumber = cardNumber;
  if (cardNumber && cardNumber !== '' && cardNumber.length >= 16) {
    formattedCardNumber = cardNumber.replace(/\D+/g, '').replace(/(\d{4})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4');
  }
  return formattedCardNumber;
};

export const formatExpirationDate = (date: string) => {
  // Remove non-digit characters
  let formattedDate = date.replace(/\D+/g, ''); 
  
  // Insert '/' after the second digit if two digits are entered
  if (formattedDate.length > 2) {
    formattedDate = `${formattedDate.slice(0, 2)}/${formattedDate.slice(2)}`;
  }
  else{
    if(parseInt(formattedDate)>12)
      formattedDate =''
  }
  // Limit to 7 characters (MM/YYYY) to prevent entering more than expected
  return formattedDate.slice(0, 7);
};


// Set items to the local storage.
export const setLocalStorage = (key: string, value: string) => {
  localStorage.setItem(key, value);
};

// Get items from the local storage.
export const getFromLocalStorage = (key: string) => {
  const data: any = localStorage.getItem(key);
  if(data === 'undefined' || data === undefined || data  === null) {
    return null;
  }
  return  JSON.parse(data) ;
};

// Sync selected location and freeSinglewash flag in local storage.
export const syncFreeSingleWashByLocation = (locationId: number) => {
  const storageData = getFromLocalStorage('CARWASH_DATA');
  if (!storageData) {
    localStorage.removeItem('freeSinglewash');
    return;
  }

  storageData.LocationId = locationId;
  setLocalStorage('CARWASH_DATA', JSON.stringify(storageData));

  const managerUserTypeId = Number(storageData?.managerUserTypeId || 0);
 // const managerLocationId = Number(storageData?.managerLocationId || 0);
  const isFreeSingleWashUser =
    managerUserTypeId === 3 ||
    (managerUserTypeId === 4 );

  if (isFreeSingleWashUser) {
    localStorage.setItem('freeSinglewash', 'true');
  } else {
    localStorage.removeItem('freeSinglewash');
  }
};

// Get the first letters from a name string
export const getFirstLettersFromName = (name: string) => {
  if (!name || typeof name !== 'string') {
    return '';
  }

  const words = name.split(' ');
  const firstLetters = words.map(word => word.charAt(0).toUpperCase()).join(' ');
  
  return firstLetters;
};

// Function to split total into dollars and cents
export const splitTotal = (price:number) => {
  const [dollars, cents] = price? price.toString().split('.'):["",""];
  return { dollars, cents };
};

// Function to format decimal minutes to "X minutes Y seconds" format
export const formatMinutesToMinutesSeconds = (decimalMinutes: number | string | null | undefined): string => {
  if (decimalMinutes === null || decimalMinutes === undefined) return '';
  const minutes = Number(decimalMinutes);
  if (isNaN(minutes)) return '';
  
  const wholeMinutes = Math.floor(minutes);
  const decimalPart = minutes - wholeMinutes;
  const seconds = Math.round(decimalPart * 60);
  
  if (wholeMinutes === 0 && seconds === 0) return '0 minutes';
  if (wholeMinutes === 0) return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  if (seconds === 0) return `${wholeMinutes} minute${wholeMinutes !== 1 ? 's' : ''}`;
  return `${wholeMinutes} minute${wholeMinutes !== 1 ? 's' : ''} ${seconds} second${seconds !== 1 ? 's' : ''}`;
};


export const getMobileOperatingSystem = () => {
  //let userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const userAgent = window.navigator.userAgent.toLowerCase();

  const platformRegex = new RegExp("(iphone|ipad|ipod|macintosh|android|windows)", "g");
  const platform = platformRegex.exec(userAgent)?.[1];

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
      return 3;
  }

  if (platform === 'android') {
      return 1;
  }

  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (platform === 'macintosh' || platform === 'iphone' || platform === 'ipad' || platform === 'ipod') {
      return 2;
  }
  return 4;
}

//Check app open in PWA or not
export const isOpenedAsPWA = () => {
  let isPWA = false
  if (window.matchMedia('(display-mode: standalone)').matches) {
    isPWA = true
  }
  return isPWA
};

export const calculateDiscountedPrice = (price : number, discountValue : number, discountType : number) => {
  let discountedPrice = 0
  if(discountType === 1) {
    discountedPrice = Number(price) - Number(discountValue)
  } else {
    const discountAmount = (price*discountValue)/100;
    discountedPrice = Number(price) - Number(discountAmount)
  }
  return discountedPrice
};

export const showTrailText = (price : number, month : number , show_html = false) => {
  let displayText = null;
  if(month === 1 && price > 0) {
    const amount = formatMoneyByLocale(price);
    displayText = (show_html) ? `First month just <span>${amount}!</span>` : `First month just ${amount}!`
  } else if(month > 1 && price > 0) {
    const amount = formatMoneyByLocale(price);
    displayText = (show_html) ? `First ${month} months just <span>${amount}!</span>` : `First ${month} months just ${amount}!`
  } else if(month === 1 && price ==0) {
    displayText = 'First month subscription is free for you!'
  } else if(month > 1 && price ==0) {
    displayText = 'First '+month+' months subscription is free for you!'
  }

  return displayText;
}


export const getBayTypes = async () => {
  try {
    const { data } = await axios.get(BayApi.GET_BAY_TYPES);
    if (data.error === 0) {
      return data.data;
    } else return [];
  } catch (e) {
    console.log(e);
    return []; 
  }
};


export const subscriptionRedirection = async (carwashname: string): Promise<string> => {
  const bayTypes: IBayType[] = await getBayTypes();

  if (bayTypes?.length > 1) {
    return `/${carwashname}/${pageURLs.selectBayType}`;
  } else if (bayTypes[0]?.bayTypeId === ServiceType.selfServe || bayTypes[0]?.bayTypeId === ServiceType.dogWash || bayTypes[0]?.bayTypeId === ServiceType.vacuumWash) {
    return `/${carwashname}/${pageURLs.selfServeSubscriptionandwashprice}?bayTypeId=${bayTypes[0]?.bayTypeId}`;
  } else {
    return `/${carwashname}/${pageURLs.subscriptionandwashprice}`;
  }
};

export function clearStorageLogout() {
    localStorage.removeItem('CARWASH_DATA');
    localStorage.removeItem('CARWASH_COMPANY_DATA');
    localStorage.removeItem('locationPermissionDenied');
    localStorage.removeItem('locationPermissionGranted');
    localStorage.removeItem('LOGO_URL_SETTINGS_DATA');
    localStorage.removeItem('fcmToken');
    sessionStorage.clear();
    window.location.href = window.location.origin + window.location.pathname; // reload at current company path
}


export function updateTokensInStorage(accessToken: string, refreshToken: string) {
  const storageData = getFromLocalStorage('CARWASH_DATA');
  if (storageData) {
    storageData.accessToken = accessToken;
    storageData.refreshToken = refreshToken;
    setLocalStorage('CARWASH_DATA', JSON.stringify(storageData));
    if (storageData.carWashID) {
      localStorage.setItem('carWashId', storageData.carWashID);
    }
  }
}

export const SHARE_CODE_KEY = 'shareCode';
export const SHARE_SUBSCRIPTION_PAYMENT_KEY = 'SHARE_SUBSCRIPTION_PAYMENT';
export const SHARE_SUBSCRIPTION_LOCATIONS_KEY = 'SHARE_SUBSCRIPTION_LOCATIONS';

/** Build payment location.state from /subscription/by-code response (same shape as buy-now). */
export const buildSharePaymentState = (subscription: any) => {
  const subscriptionType = subscription?.subscriptionType ?? subscription?.SubscriptionType;
  const fromTab = Number(subscriptionType) === 1 ? 'singlewash' : 'subscriptions';

  return {
    origin: 'ProductSignUp',
    fromTab,
    subscriptionID: subscription?.subscriptionID ?? subscription?.subscriptionId ?? subscription?.SubscriptionID ?? subscription?.planId ?? subscription?.PlanId,
    washName: subscription?.washName ?? subscription?.WashName,
    price: subscription?.price ?? subscription?.Price,
    numberOfWashes: subscription?.numberOfWashes ?? subscription?.NumberOfWashes,
    subscriptionType,
    isTrial: subscription?.isTrial ?? subscription?.IsTrial,
    trialPeriod: subscription?.trialPeriod ?? subscription?.TrialPeriod,
    trialAmount: subscription?.trialAmount ?? subscription?.TrialAmount,
    TrialSubscriptionAvailable:
      subscription?.TrialSubscriptionAvailable ?? subscription?.trialSubscriptionAvailable ?? true,
    ServiceType: subscription?.ServiceType ?? subscription?.serviceType ?? subscription?.bayType ?? ServiceType.bayTunnel,
    FamilyDiscount: subscription?.FamilyDiscount ?? subscription?.familyDiscount ?? subscription?.userFamilyDiscount,
  };
};

/** Normalize by-code `locations` into locationId numbers. */
export const extractShareLocationIds = (locations: any): number[] => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }
  return locations
    .map((item: any) => {
      if (typeof item === 'number') return item;
      if (typeof item === 'string' && item.trim() !== '' && !isNaN(Number(item))) return Number(item);
      return Number(
        item?.locationId ??
        item?.LocationId ??
        item?.LocationID ??
        item?.locationID ??
        item?.id ??
        0
      );
    })
    .filter((id: number) => !!id && !isNaN(id));
};

/** Map by-code API location objects to SwitchLocation list shape. */
export const mapShareApiLocations = (locations: any) => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }
  return locations
    .map((item: any) => {
      const locationId = Number(
        item?.locationId ??
        item?.LocationId ??
        item?.LocationID ??
        item?.locationID ??
        item?.id ??
        0
      );
      const locationName = item?.locationName ?? item?.LocationName ?? '';
      const address = item?.Address ?? item?.address ?? item?.address1 ?? '';
      return {
        locationId,
        locationName,
        isActive: 1,
        hasDefaultAddress: 0,
        address1: address || null,
      };
    })
    .filter((loc) => loc.locationId > 0);
};

const getStoredShareLocationsRaw = (): any[] => {
  const raw = localStorage.getItem(SHARE_SUBSCRIPTION_LOCATIONS_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const saveShareSubscriptionFlow = (shareCode: string, paymentState: object, locations?: any) => {
  localStorage.setItem(SHARE_CODE_KEY, shareCode);
  localStorage.setItem(SHARE_SUBSCRIPTION_PAYMENT_KEY, JSON.stringify(paymentState));
  if (Array.isArray(locations) && locations.length > 0) {
    localStorage.setItem(SHARE_SUBSCRIPTION_LOCATIONS_KEY, JSON.stringify(locations));
  } else {
    localStorage.removeItem(SHARE_SUBSCRIPTION_LOCATIONS_KEY);
  }
};

export const getPendingSharePaymentState = () => {
  const raw = localStorage.getItem(SHARE_SUBSCRIPTION_PAYMENT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const getShareSubscriptionLocationIds = (): number[] => {
  return extractShareLocationIds(getStoredShareLocationsRaw());
};

export const getShareSubscriptionMappedLocations = () => {
  return mapShareApiLocations(getStoredShareLocationsRaw());
};

/** Map location-fetch API row to SwitchLocation list shape. */
export const mapFetchedLocationToDisplay = (location: any) => {
  if (location?.LocationName || location?.LocationID) {
    return mapShareApiLocations([location])[0];
  }

  const addressArray = [];
  if (location.hasDefaultAddress) {
    addressArray.push(location.companyAddress1);
    addressArray.push(location.companyAddress2);
    addressArray.push(location.companyCity);
    addressArray.push(location.companyState);
    addressArray.push(location.companyZipCode);
  } else {
    addressArray.push(location.address1);
    addressArray.push(location.address2);
    addressArray.push(location.city);
    addressArray.push(location.state);
    addressArray.push(location.zipCode);
  }

  const address = addressArray.filter((el) => el).join(', ');

  return {
    locationId: location.locationId,
    locationName: location.locationName,
    isActive: location.isActive,
    hasDefaultAddress: location.hasDefaultAddress,
    address1: address,
    countryCode: location.countryCode,
    currency: location.currency,
    locale: location.locale,
    timezone: location.timezone,
    dialCode: location.dialCode,
    dateFormat: location.dateFormat,
    stripeCountry: location.stripeCountry,
  };
};

/** Resolve product-signup locations: intersect fetch API with by-code locations, fallback to by-code list. */
export const resolveProductSignupLocations = (fetchedLocations: any[] = []) => {
  const allowedLocationIds = getShareSubscriptionLocationIds();
  const shareApiLocations = getShareSubscriptionMappedLocations();

  if (allowedLocationIds.length === 0) {
    return { locations: [], isProductShareFlow: !!localStorage.getItem(SHARE_CODE_KEY) };
  }

  const filteredFetched = fetchedLocations.filter((loc) =>
    allowedLocationIds.includes(Number(loc.locationId))
  );

  if (filteredFetched.length > 0) {
    return {
      locations: filteredFetched.map(mapFetchedLocationToDisplay),
      isProductShareFlow: true,
    };
  }

  return { locations: shareApiLocations, isProductShareFlow: true };
};

export const PRODUCT_SIGNUP_DATA_KEY = 'product_signup_data';

/** Fetch share-code subscription, save flow data, redirect logged-in users to payment. */
export const initProductSignUpShareFlow = async (
  shareCode: string,
  carwashname: string,
  navigate: (path: string, options?: { state?: object }) => void,
  toaster: { toastError: (msg: string) => void }
): Promise<boolean> => {
  if (!shareCode) {
    toaster.toastError("Invalid subscription link.");
    navigate("/" + carwashname + pageURLs.root);
    return false;
  }

  const { data } = await axios.get(SubscriptionandwashpriceApi.GET_SUBSCRIPTION_BY_CODE + shareCode);
  if (data?.error !== 0 || !data?.data) {
    toaster.toastError(data?.message || "Unable to load subscription.");
    navigate("/" + carwashname + pageURLs.root);
    return false;
  }

  const plan = data.data;
  const isActive = Number(plan?.isActive ?? plan?.IsActive ?? 0);
  if (isActive !== 1) {
    toaster.toastError("This membership plan is no longer available.");
    navigate("/" + carwashname + pageURLs.root);
    return false;
  }

  const paymentState = buildSharePaymentState(plan);
  if (!paymentState.subscriptionID) {
    toaster.toastError("Invalid subscription details.");
    navigate("/" + carwashname + pageURLs.root);
    return false;
  }

  const storageData = getFromLocalStorage('CARWASH_DATA');
  if (storageData?.accessToken) {
    navigate("/" + carwashname + "/" + pageURLs.payment, { state: paymentState });
    return false;
  }

  saveShareSubscriptionFlow(shareCode, paymentState, plan?.locations ?? plan?.Locations);
  return true;
};

export const clearShareSubscriptionFlow = () => {
  localStorage.removeItem(SHARE_CODE_KEY);
  localStorage.removeItem(SHARE_SUBSCRIPTION_PAYMENT_KEY);
  localStorage.removeItem(SHARE_SUBSCRIPTION_LOCATIONS_KEY);
};

/** If a share-code purchase is pending, navigate to payment and clear stored flow. */
export const navigateToSharePaymentIfPending = (
  navigate: (path: string, options?: { state?: object }) => void,
  carwashname: string
): boolean => {
  const paymentState = getPendingSharePaymentState();
  if (!paymentState?.subscriptionID) {
    return false;
  }
  clearShareSubscriptionFlow();
  navigate(`/${carwashname}/${pageURLs.payment}`, { state: paymentState });
  return true;
};